import React, { useEffect, useState, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { metricsAPI } from '../../services/metricsApi';

// ── SVG Donut Chart ──────────────────────────────────────────────────────
function DonutChart({ percent, size = 80, strokeWidth = 8, color = '#4f46e5' }) {
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={radius}
        fill="none" stroke="#e5e7eb" strokeWidth={strokeWidth} />
      <circle cx={size / 2} cy={size / 2} r={radius}
        fill="none" stroke={color} strokeWidth={strokeWidth}
        strokeDasharray={circumference} strokeDashoffset={offset}
        strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      <text x="50%" y="50%" textAnchor="middle" dy="0.35em"
        style={{ transform: 'rotate(90deg)', transformOrigin: 'center', fontSize: '14px', fontWeight: 600, fill: '#1f2937' }}>
        {Math.round(percent)}%
      </text>
    </svg>
  );
}

// ── Skills Radar Chart (SVG polygon) ─────────────────────────────────────
function RadarChart({ modules, size = 220 }) {
  const cx = size / 2, cy = size / 2, maxR = size / 2 - 20;
  const n = modules.length;
  if (n < 3) return null;

  const angleStep = (2 * Math.PI) / n;
  const points = modules.map((m, i) => {
    const angle = i * angleStep - Math.PI / 2;
    const r = (m.mastery_score || 0) * maxR;
    return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
  });
  const bgPoints = modules.map((_, i) => {
    const angle = i * angleStep - Math.PI / 2;
    return `${cx + maxR * Math.cos(angle)},${cy + maxR * Math.sin(angle)}`;
  });

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <polygon points={bgPoints.join(' ')} fill="none" stroke="#e5e7eb" strokeWidth="1" />
      {[0.25, 0.5, 0.75].map(f => {
        const ring = modules.map((_, i) => {
          const angle = i * angleStep - Math.PI / 2;
          const r = f * maxR;
          return `${cx + r * Math.cos(angle)},${cy + r * Math.sin(angle)}`;
        });
        return <polygon key={f} points={ring.join(' ')} fill="none" stroke="#f3f4f6" strokeWidth="1" />;
      })}
      {modules.map((_, i) => {
        const angle = i * angleStep - Math.PI / 2;
        return <line key={i} x1={cx} y1={cy} x2={cx + maxR * Math.cos(angle)} y2={cy + maxR * Math.sin(angle)} stroke="#e5e7eb" strokeWidth="1" />;
      })}
      <polygon points={points.join(' ')} fill="rgba(79,70,229,0.2)" stroke="#4f46e5" strokeWidth="2" />
      {modules.map((m, i) => {
        const angle = i * angleStep - Math.PI / 2;
        const labelR = maxR + 14;
        return (
          <text key={i} x={cx + labelR * Math.cos(angle)} y={cy + labelR * Math.sin(angle)}
            textAnchor="middle" fontSize="9" fill="#6b7280">
            {(m.module_name || m.module_id || '').slice(0, 10)}
          </text>
        );
      })}
    </svg>
  );
}

// ── Activity Heatmap (GitHub-style) ──────────────────────────────────────
function ActivityHeatmap({ timeline }) {
  const cellSize = 12, gap = 2;
  const data = (timeline || []).slice(-90);
  const weeks = Math.ceil(data.length / 7);
  const width = weeks * (cellSize + gap) + 20;
  const height = 7 * (cellSize + gap) + 20;

  function intensityColor(sessions) {
    if (!sessions) return '#f3f4f6';
    if (sessions === 1) return '#c7d2fe';
    if (sessions <= 3) return '#818cf8';
    return '#4338ca';
  }

  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      {data.map((d, i) => {
        const week = Math.floor(i / 7);
        const dayOfWeek = i % 7;
        return (
          <rect key={i}
            x={week * (cellSize + gap)}
            y={dayOfWeek * (cellSize + gap)}
            width={cellSize} height={cellSize} rx={2}
            fill={intensityColor(d.sessions)}
            title={`${d.date}: ${d.sessions} sessions`}
          />
        );
      })}
    </svg>
  );
}

// ── Module Progress Grid ─────────────────────────────────────────────────
function ModuleGrid({ modules }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
      {(modules || []).map((m, i) => {
        let bg = '#e5e7eb';
        if (m.completed) bg = '#22c55e';
        else if (m.mastery_score > 0) bg = '#eab308';
        return (
          <div key={i} title={`${m.module_name || m.module_id}: ${Math.round((m.mastery_score || 0) * 100)}%`}
            style={{ width: 28, height: 28, borderRadius: 4, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, color: '#fff', fontWeight: 600 }}>
            {m.completed ? '✓' : m.mastery_score > 0 ? '…' : ''}
          </div>
        );
      })}
    </div>
  );
}

// ── Main Page ────────────────────────────────────────────────────────────
export default function ProgressDashboardV2() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [activity, setActivity] = useState(null);
  const [skills, setSkills] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    Promise.all([
      metricsAPI.getLearnerProgress(token),
      metricsAPI.getLearnerActivity(token, 90),
      metricsAPI.getLearnerSkills(token),
    ]).then(([p, a, s]) => {
      setProgress(p);
      setActivity(a);
      setSkills(s);
    }).catch(console.error)
      .finally(() => setLoading(false));
  }, [token]);

  const allModules = useMemo(() => {
    if (!skills?.skills) return [];
    return skills.skills.flatMap(d => d.modules || []);
  }, [skills]);

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 18, color: '#6b7280' }}>Loading your progress...</div>
      </div>
    );
  }

  if (!progress) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <div style={{ fontSize: 18, color: '#ef4444' }}>Unable to load progress data.</div>
      </div>
    );
  }

  const { domains, overall } = progress;

  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <button
          onClick={() => navigate('/dashboard')}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '7px 14px',
            border: '1px solid #e5e7eb',
            borderRadius: 8,
            background: '#f9fafb',
            color: '#374151',
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
          }}
        >
          ← Back to Dashboard
        </button>
      </div>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 8, color: '#111827' }}>My Progress</h1>
      <p style={{ color: '#6b7280', marginBottom: 32 }}>
        {overall.total_xp.toLocaleString()} XP earned · {overall.badges?.length || 0} badges · {overall.certificates?.length || 0} certificates
      </p>

      {/* Domain Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 20, marginBottom: 40 }}>
        {domains.map(d => (
          <div key={d.domain} style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', border: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
              <div>
                <h3 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: '#111827' }}>{d.display_name}</h3>
                <span style={{ fontSize: 12, color: '#9ca3af' }}>{d.modules_completed}/{d.modules_total} modules</span>
              </div>
              <DonutChart percent={d.completion_pct} size={64} strokeWidth={6} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 12 }}>
              <div style={{ textAlign: 'center', padding: '8px 4px', background: '#f9fafb', borderRadius: 8 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#4f46e5' }}>{Math.round(d.mastery_avg * 100)}%</div>
                <div style={{ fontSize: 10, color: '#6b7280' }}>Mastery</div>
              </div>
              <div style={{ textAlign: 'center', padding: '8px 4px', background: '#f9fafb', borderRadius: 8 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#f59e0b' }}>{d.streak_days}</div>
                <div style={{ fontSize: 10, color: '#6b7280' }}>Day Streak</div>
              </div>
              <div style={{ textAlign: 'center', padding: '8px 4px', background: '#f9fafb', borderRadius: 8 }}>
                <div style={{ fontSize: 18, fontWeight: 700, color: '#10b981' }}>{d.code_labs_completed}/{d.code_labs_total}</div>
                <div style={{ fontSize: 10, color: '#6b7280' }}>Labs</div>
              </div>
            </div>

            {/* Capstone Status */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: d.capstone_status === 'passed' ? '#ecfdf5' : '#f9fafb', borderRadius: 8, marginBottom: 8 }}>
              <span style={{ fontSize: 16 }}>
                {d.capstone_status === 'passed' ? '🎓' : d.capstone_status === 'submitted' ? '📤' : d.capstone_status === 'graded' ? '📋' : '🎯'}
              </span>
              <span style={{ fontSize: 12, fontWeight: 500, color: '#374151' }}>
                {d.capstone_status === 'not_started' && 'Start Capstone'}
                {d.capstone_status === 'submitted' && 'Capstone Submitted — Awaiting Review'}
                {d.capstone_status === 'graded' && 'View Feedback'}
                {d.capstone_status === 'passed' && 'Certificate Earned!'}
              </span>
            </div>

            <div style={{ display: 'flex', gap: 16, fontSize: 11, color: '#6b7280' }}>
              <span>💬 {d.forum_posts} posts</span>
              <span>📝 {d.notes_count} notes</span>
              <span>⏱ {Math.round(d.time_spent_minutes / 60)}h spent</span>
            </div>
          </div>
        ))}
      </div>

      {/* Activity Heatmap */}
      <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: 32 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#111827' }}>Activity (Last 90 Days)</h2>
        <div style={{ overflowX: 'auto' }}>
          <ActivityHeatmap timeline={activity?.timeline} />
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', fontSize: 11, color: '#6b7280' }}>
          <span>Less</span>
          {['#f3f4f6', '#c7d2fe', '#818cf8', '#4338ca'].map(c => (
            <span key={c} style={{ width: 12, height: 12, borderRadius: 2, background: c, display: 'inline-block' }} />
          ))}
          <span>More</span>
        </div>
      </div>

      {/* Skills Radar + Module Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, marginBottom: 32 }}>
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#111827' }}>Skills Radar</h2>
          {allModules.length >= 3 ? (
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <RadarChart modules={allModules.slice(0, 12)} />
            </div>
          ) : (
            <p style={{ color: '#9ca3af', fontSize: 13 }}>Complete more modules to see your skill radar.</p>
          )}
        </div>

        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#111827' }}>Module Progress</h2>
          {(skills?.skills || []).map(domain => (
            <div key={domain.domain} style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{domain.display_name}</div>
              <ModuleGrid modules={domain.modules} />
            </div>
          ))}
        </div>
      </div>

      {/* Badges & Certificates */}
      {(overall.badges?.length > 0 || overall.certificates?.length > 0) && (
        <div style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#111827' }}>Achievements</h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {(overall.badges || []).map((b, i) => (
              <div key={i} style={{ padding: '6px 12px', background: '#fef3c7', borderRadius: 16, fontSize: 12, fontWeight: 500 }}>
                🏅 {b.name || b}
              </div>
            ))}
            {(overall.certificates || []).map((c, i) => (
              <div key={i} style={{ padding: '6px 12px', background: '#ecfdf5', borderRadius: 16, fontSize: 12, fontWeight: 500 }}>
                🎓 {c.domain}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
